'use client';
import React, { useEffect, useState } from 'react';
import ProductCarousel from '@/components/ui/ProductCarousel';
import { ProductBase } from '@/types/product';
import { OtherCustomersAlsoBought } from '@/types/strapi';
import { getProductsByIds } from '@/hooks/useProducts';
import Loader from '@/components/ui/Loader';
import { BreadcrumbMenu } from '@/types/menu';

interface OtherProductsSectionProps {
  onFavoriteToggle?: (id: string) => void;
  otherProducts: ProductBase[];
  otherCustomersData?: OtherCustomersAlsoBought | null;
}

const OtherProductsSection: React.FC<OtherProductsSectionProps> = ({
  onFavoriteToggle,
  otherProducts,
  otherCustomersData
}) => {
  const [strapiProducts, setStrapiProducts] = useState<ProductBase[]>([]);
  const [isLoadingStrapi, setIsLoadingStrapi] = useState(false);

  // Obtener productos de Strapi si hay datos disponibles
  useEffect(() => {
    const fetchStrapiProducts = async () => {
      if (!otherCustomersData?.productos || otherCustomersData.productos.length === 0) {
        return;
      }

      setIsLoadingStrapi(true);
      try {
        const productIds = otherCustomersData.productos.map(p => p.id_producto_prestashop.toString());
        const fetchedProducts = await getProductsByIds(productIds);
        setStrapiProducts(fetchedProducts);
      } catch (error) {
        console.error('Error fetching other customers products:', error);
      } finally {
        setIsLoadingStrapi(false);
      }
    };

    fetchStrapiProducts();
  }, [otherCustomersData?.productos]);

  // Usar productos de Strapi si están disponibles, sino usar otherProducts
  const displayProducts = strapiProducts.length > 0 ? strapiProducts : otherProducts;
  const isLoading = isLoadingStrapi && strapiProducts.length === 0;

  const breadcrumbMenu: BreadcrumbMenu = {
    menuLevel1: {
      id: 0,
      description: '',
      level: 0,
      parent: 0,
      name: 'Otros clientes también compraron',
      slug: 'otros-clientes-tambien-compraron',
      pageNumbers: 0,
      url: '',
    },
  };

  return (
    <div data-component-id="src/components/sections/OtherProductsSection.tsx:64:4" data-component-path="src/components/sections/OtherProductsSection.tsx" data-component-line="64" data-component-file="OtherProductsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pt-4%22%7D" className="pt-4">
      <div data-component-id="src/components/sections/OtherProductsSection.tsx:65:6" data-component-path="src/components/sections/OtherProductsSection.tsx" data-component-line="65" data-component-file="OtherProductsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-2%20text-left%22%7D" className="mb-2 text-left">
        <p data-component-id="src/components/sections/OtherProductsSection.tsx:66:8" data-component-path="src/components/sections/OtherProductsSection.tsx" data-component-line="66" data-component-file="OtherProductsSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-global-1%20text-%5B40px%5D%20leading-%5B48px%5D%20mb-1%22%2C%22textContent%22%3A%22OTROS%20CLIENTES%20TAMBI%C3%89N%20COMPRARON%22%7D"
          className="text-global-1 text-[40px] leading-[48px] mb-1"
          style={{
            fontFamily: "'TT_Norms_Pro-Extra-Black', Helvetica",
            fontWeight: '900',
          }}
        >
          OTROS CLIENTES TAMBIÉN COMPRARON
        </p>
        <div data-component-id="src/components/sections/OtherProductsSection.tsx:75:8" data-component-path="src/components/sections/OtherProductsSection.tsx" data-component-line="75" data-component-file="OtherProductsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-global-9%20rounded-none%20px-4%20w-fit%20mb-6%22%7D" className="bg-global-9 rounded-none px-4 w-fit mb-6">
          <span data-component-id="src/components/sections/OtherProductsSection.tsx:76:10" data-component-path="src/components/sections/OtherProductsSection.tsx" data-component-line="76" data-component-file="OtherProductsSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-global-1%20text-%5B22px%5D%20leading-%5B27px%5D%22%2C%22textContent%22%3A%22Seguro%20que%20te%20interesa%22%7D" 
            className="text-global-1 text-[22px] leading-[27px]"
            style={{
              fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
              fontWeight: '400'
            }}
          >
            Seguro que te interesa
          </span>
        </div>
        
        {isLoading ? (
          <div data-component-id="src/components/sections/OtherProductsSection.tsx:88:10" data-component-path="src/components/sections/OtherProductsSection.tsx" data-component-line="88" data-component-file="OtherProductsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-center%20py-8%22%7D" className="flex justify-center py-8">
            <Loader data-component-id="src/components/sections/OtherProductsSection.tsx:89:12" data-component-path="src/components/sections/OtherProductsSection.tsx" data-component-line="89" data-component-file="OtherProductsSection.tsx" data-component-name="Loader" data-component-content="%7B%22elementName%22%3A%22Loader%22%7D" />
          </div>
        ) : (
          <ProductCarousel data-component-id="src/components/sections/OtherProductsSection.tsx:92:10" data-component-path="src/components/sections/OtherProductsSection.tsx" data-component-line="92" data-component-file="OtherProductsSection.tsx" data-component-name="ProductCarousel" data-component-content="%7B%22elementName%22%3A%22ProductCarousel%22%2C%22className%22%3A%22mb-8%22%7D"
            breadcrumbMenu={breadcrumbMenu}
            items={displayProducts}
            className="mb-8"
            onFavoriteToggle={onFavoriteToggle}
            defaultVisibleCount={5}
            showNavigation={true}
            productTitleAsParagraph
          />
        )}
      </div>
    </div>
  );
};

export default OtherProductsSection; 